import { useCallback, useEffect, useRef, useState } from "react";
import { FormControl } from "react-bootstrap";
import { Control, Controller, ControllerRenderProps, UseFormSetValue } from "react-hook-form";
import { isValidEmail } from "src/utils";

interface IProps {
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    name: string;
    defaultValue?: string;
    placeholder?: string;
    disabled?: boolean;
    onBlur?: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
    isInvalid?: boolean;
    className?: string;
    maxLength?: number;
    isPatient?: boolean;
    getPatientExistsByEmail?: (
        email: string,
        skipNotify?: boolean,
    ) => Promise<{ exists: boolean; accountPatientId?: number }>;
    setExistingPatient?: (exist: boolean) => void;
}

const EmailFormController: React.FC<IProps> = ({
    control,
    setValue,
    name,
    defaultValue,
    placeholder,
    disabled,
    onBlur,
    isInvalid,
    className,
    isPatient,
    getPatientExistsByEmail,
    setExistingPatient,
}) => {
    // Track the last validated email to prevent redundant API calls
    const [lastValidatedEmail, setLastValidatedEmail] = useState<string>("");
    // Track if validation is in progress
    const [isValidating, setIsValidating] = useState(false);

    // Use a timeout for debouncing
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Separate the validation logic from rendering
    const validateEmail = useCallback(
        (email: string) => {
            // Don't validate if:
            // - No validation function provided
            // - Already validating this email
            // - Already validated this exact email
            // - Email is invalid
            if (
                !getPatientExistsByEmail ||
                isValidating ||
                email === lastValidatedEmail ||
                !isValidEmail(email)
            ) {
                return;
            }

            setIsValidating(true);

            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Debounce the API call by 300ms
            timeoutRef.current = setTimeout(() => {
                getPatientExistsByEmail(email.toLowerCase())
                    .then((existing) => {
                        if (!isPatient && existing.accountPatientId) {
                            setValue("accountPatientId", existing.accountPatientId);
                        }
                        setExistingPatient && setExistingPatient(existing.exists);
                        setLastValidatedEmail(email);
                    })
                    .catch(console.error)
                    .finally(() => {
                        setIsValidating(false);
                    });
            }, 300);
        },
        [
            getPatientExistsByEmail,
            isValidating,
            lastValidatedEmail,
            isPatient,
            setValue,
            setExistingPatient,
        ],
    );

    // Clean up timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const renderEmailInput = useCallback(
        ({ field }: { field: ControllerRenderProps<any, any> }) => (
            <FormControl
                name={field.name}
                id={field.name}
                type="email"
                autoComplete="email"
                isInvalid={isInvalid}
                placeholder={placeholder === undefined ? "Email" : placeholder}
                disabled={disabled}
                onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value);
                    validateEmail(value);
                }}
                value={field.value || ""}
                onBlur={onBlur}
                className={className}
            />
        ),
        [className, disabled, isInvalid, onBlur, placeholder, validateEmail],
    );

    return (
        <Controller
            defaultValue={defaultValue}
            control={control}
            name={name}
            render={renderEmailInput}
        />
    );
};

export default EmailFormController;
